import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import BaseOverviewStore, {
    type ApiResponse,
    type BaseStoreState,
} from '../../../shared/stores/baseOverviewStore';
import { MatenRequestResponse } from '../../../shared';

interface KnowledgeStoreState extends BaseStoreState {
    filterType: any;
    selectedStore: number | null;
}

/**
 * Load all knowledges and make it possible to manage them
 */
class KnowledgeOverviewStore extends BaseOverviewStore<any> {
    protected storageKey(): string {
        return 'knowledge';
    }

    // Implement required abstract methods
    protected defaultSortField(): string {
        return 'id';
    }

    protected getApiEndpoint(): string {
        return 'knowledge';
    }

    protected getApiPath(): string {
        return 'module/query';
    }

    // Knowledge-specific properties
    public knowledgeStores: Array<any> = [];
    public filterType: any = null;
    public available_categories: Array<{ id: number; name: string }> = [];
    public filterCategories: Array<{ id: number; name: string }> = [];
    public selectedStore: number | null = null;
    public s3url: string = '';

    constructor(sdk: any) {
        super(sdk);

        // Make knowledge-specific properties observable
        makeObservable(this, {
            knowledgeStores: observable,
            filterType: observable,
            available_categories: observable,
            filterCategories: observable,
            selectedStore: observable,
            s3url: observable,
            onDeleteKnowledge: action,
            switchStore: action,
            applyFilterType: action,
            allKnowledgeModules: computed,
            allKnowledgeStores: computed,
            currentKnowledgeStoreIndex: computed,
            currentKnowledgeStore: computed,
        });

        // Re-load from storage now that observables are set up
        this.loadFromLocalStorage();
    }

    protected buildApiParams(page: number): any {
        return {
            sortvalue: this.sortField,
            sort: this.sortOrder === 1 ? 'asc' : 'desc',
            search: this.searchValue || '',
            page,
            store_id: this.selectedStore || null,
            ...(this.filterType ? { filter: { type: 'type', value: this.filterType } } : {}),
        };
    }

    protected processApiResponse(response: ApiResponse): any[] {
        runInAction(() => {
            const { s3url, stores, selectedStore } = response.json;
            this.s3url = s3url || '';
            this.knowledgeStores = stores || [];

            // Set the selected store if it is provided
            if (this.selectedStore !== selectedStore) {
                this.selectedStore = selectedStore;
            }
        });
        return response.json.knowledge || [];
    }

    protected getStorageState(): KnowledgeStoreState {
        return {
            sortOrder: this.sortOrder,
            sortField: this.sortField,
            searchValue: this.searchValue,
            filterType: this.filterType,
            selectedStore: this.selectedStore,
        };
    }

    protected applyStorageState(state: KnowledgeStoreState): void {
        runInAction(() => {
            this.sortOrder = state.sortOrder || 1;
            this.sortField = state.sortField || this.defaultSortField();
            this.searchValue = state.searchValue || '';
            this.filterType = state.filterType || null;
            this.selectedStore = state.selectedStore || null;
        });
    }

    public async init(): Promise<void> {
        this.knowledgeStores = [];
        this.fetchList();
    }

    // Knowledge-specific methods
    public applyFilterType(type: string | null): void {
        this.filterType = type;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public switchStore(storeId: number): void {
        if (!storeId || storeId === this.selectedStore) return;

        runInAction(() => {
            this.selectedStore = storeId;
        });

        this.saveToLocalStorage();
        this.fetchList();
    }

    public async onDeleteKnowledge(knowledge: any): Promise<MatenRequestResponse> {
        if (knowledge?.id) {
            const result = await this.sdk.fetchApi('knowledge', 'module/delete', {
                knowledge_id: knowledge.id,
            });

            if (result?.status === 200) {
                this.fetchList();
                return { success: true, status: 200, data: result.json };
            }
        }

        return { success: false, status: 400 };
    }

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (dialog === 'knowledge' && event?.event === 'SAVED') {
            await this.fetchList(this.latestPage);
        } else {
            await super.onDialogLeave(dialog, event);
        }
    }

    // Computed properties
    get allKnowledgeStores(): Array<any> | null {
        return this.knowledgeStores.map((store: any) => ({
            id: store.id,
            label: store.label,
            shortDescription: store.short_description,
            value: `store-${store.id}`,
        }));
    }

    get currentKnowledgeStoreIndex(): number {
        return this.knowledgeStores.findIndex((store: any) => store.id === this.selectedStore);
    }

    get currentKnowledgeStore(): any {
        return this.knowledgeStores[this.currentKnowledgeStoreIndex] || null;
    }

    get allKnowledgeModules(): Array<any> | null {
        if (!this.list) return null;
        return this.list
            .filter((item: any) => {
                return true;
            })
            .map((item: any) => ({
                ...item,
                image: item.image ? `${this.s3url}${item.image}.jpeg` : null,
                icon: item.type === 'pdf' ? 'pi-file-pdf' : 'pi-database',
            }));
    }

    // Compatibility aliases for existing code
    get knowledgeList(): Array<any> {
        return this.list;
    }

    get knowledgeListLoading(): boolean {
        return this.listLoading;
    }

    public fetchKnowledgeList(page: number = 1): Promise<void> {
        return this.fetchList(page);
    }
}

export default KnowledgeOverviewStore;
